import { Alert, Button, Group, MultiSelect, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { usePermission } from "@how/app-kit";
import { useProducts } from "../store/store-queries";
import { useEventAddOns, useSetEventAddOns } from "./event-queries";

/**
 * An event's add-ons (ADR 0047 step 3): products from the store's catalog that members can pick when they register,
 * handed over at the event. Shown only with the Commerce module and the permission to read the store; changed by
 * whoever manages events. A shipped-only product cannot be an add-on.
 */
export function EventAddOnsSection({ eventId }: { readonly eventId: string }) {
  const canSee = usePermission("commerce.read", { module: "commerce" });
  const canManage = usePermission("events.manage", { module: "commerce", write: true });
  const products = useProducts();
  const addOns = useEventAddOns(canSee.data === true ? eventId : null);
  const save = useSetEventAddOns();
  const [chosen, setChosen] = useState<string[]>([]);
  useEffect(() => {
    if (addOns.data !== undefined) setChosen(addOns.data);
  }, [addOns.data]);

  if (canSee.data !== true) return null;
  const options = (products.data ?? [])
    .filter((p) => p.fulfilment !== "ship" && (p.isActive || chosen.includes(p.id)))
    .map((p) => ({
      value: p.id,
      label: p.inShop ? p.name : `${p.name} (this event only)`,
    }));
  const changed = JSON.stringify(chosen) !== JSON.stringify(addOns.data ?? []);
  const manage = canManage.data === true;

  return (
    <section>
      <Stack gap="xs">
        <Title order={4}>Add-ons</Title>
        <Text size="sm" c="dimmed">
          Things members can add when they register, such as a meal or a shirt, handed over at the
          event. Choose from Store → Products; a product made only for this event can be kept out of
          the Shop there.
        </Text>
        <MultiSelect
          aria-label="Add-ons"
          placeholder={options.length === 0 ? "No products in the store yet" : "No add-ons"}
          data={options}
          value={chosen}
          disabled={!manage || products.isPending}
          searchable
          maxValues={20}
          onChange={setChosen}
        />
        {save.error ? (
          <Alert color="red" variant="light">
            {save.error.message}
          </Alert>
        ) : null}
        {manage && changed ? (
          <Group>
            <Button
              size="xs"
              loading={save.isPending}
              onClick={() => {
                save.mutate(
                  { eventId, productIds: chosen },
                  {
                    onSuccess: () => {
                      notifications.show({ message: "Add-ons saved" });
                    },
                  },
                );
              }}
            >
              Save add-ons
            </Button>
          </Group>
        ) : null}
      </Stack>
    </section>
  );
}
